import { useEffect, useState } from "react";
import { InputText } from "primereact/inputtext";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ColumnGroup } from "primereact/columngroup";
import { Row } from "primereact/row";
import { InputNumber } from "primereact/inputnumber";
import { fetchData } from "../../../Services/GlobalService";
import type {
  ConstructFormData,
  ConstructType,
  KpiRow,
  PerformanceTarget,
  EarningTarget,
  EarningType,
  EarningCreditDays,
} from "./Construct";
import { MultiSelect } from "primereact/multiselect";

/* ── API config ── */
const ControllerDbsip = "ConstructDBSIP";
const EndpointDbsip = { GetOrderModeData: "GetOrderModeData" };

interface Props {
  formData: ConstructFormData;
  setFormData: React.Dispatch<React.SetStateAction<ConstructFormData>>;
  constructType: ConstructType;
}

const payoutRowStyle: React.CSSProperties = {
  backgroundColor: "#e8e8e8",
  fontWeight: 700,
  fontSize: "0.82rem",
};

export default function DefinitionTab({
  formData,
  setFormData,
  constructType,
}: Props) {
  const isB2C = constructType === "B2C";
  const sectionKey =
    constructType === "B2C"
      ? "b2c"
      : constructType === "HDSSD"
        ? "hsssd"
        : "industrial";

  const APPLICABLE_OPTIONS_B2C = ["Segment", "Anmol Ratan"];
  const APPLICABLE_OPTIONS = ["Segment"];
  const BASIS_OPTIONS = ["Yearly", "Quarterly", "Weekly"];
  const B2C_TYPE_OPTIONS = ["Volume", "Count", "Value"];
  const ODT_OPTIONS = ["YES", "NO"];
  const EARNING_TYPE_OPTIONS: EarningType[] = ["Credit Days", "Earning %"];

  const [orderModeOptions, setOrderModeOptions] = useState<
    { ORD_MODE_ID: string; ORD_MODE_DESC: string }[]
  >([]);

  useEffect(() => {
    const fetchOrderMode = async () => {
      try {
        const res = await fetchData(
          ControllerDbsip,
          EndpointDbsip.GetOrderModeData,
          ``,
        );
        setOrderModeOptions(res?.Data ?? []);
      } catch {
        setOrderModeOptions([]);
      }
    };
    fetchOrderMode();
  }, []);

  /* -------- UPDATE KPI -------- */
  const updateKpi = (id: string, field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [sectionKey]: {
        ...prev[sectionKey],
        kpis: (prev[sectionKey].kpis as KpiRow[]).map((k) =>
          k.id === id ? { ...k, [field]: value } : k,
        ),
      },
    }));
  };

  const updatePerformance = (
    id: string,
    key: keyof PerformanceTarget,
    value: number,
  ) => {
    setFormData((prev) => ({
      ...prev,
      [sectionKey]: {
        ...prev[sectionKey],
        kpis: (prev[sectionKey].kpis as KpiRow[]).map((k) =>
          k.id === id
            ? { ...k, performance: { ...k.performance, [key]: value } }
            : k,
        ),
      },
    }));
  };

  const updateEarning = (
    id: string,
    key: keyof EarningTarget,
    value: number,
  ) => {
    setFormData((prev) => ({
      ...prev,
      b2c: {
        ...prev.b2c,
        kpis: prev.b2c.kpis.map((k) =>
          k.id === id ? { ...k, earning: { ...k.earning!, [key]: value } } : k,
        ),
      },
    }));
  };

  const updateEarningCreditDays = (
    id: string,
    key: keyof EarningCreditDays,
    value: number,
  ) => {
    setFormData((prev) => ({
      ...prev,
      b2c: {
        ...prev.b2c,
        kpis: prev.b2c.kpis.map((k) =>
          k.id === id
            ? {
                ...k,
                earningCreditDays: {
                  ...(k.earningCreditDays ?? {
                    threshold: 0,
                    target: 0,
                    stretch: 0,
                  }),
                  [key]: value,
                },
              }
            : k,
        ),
      },
    }));
  };

  const updatePayoutTable = (
    sk: "hsssd" | "industrial",
    field: keyof PerformanceTarget,
    value: number,
  ) => {
    setFormData((prev) => ({
      ...prev,
      [sk]: {
        ...(prev[sk] as any),
        payoutTable: {
          ...(prev[sk] as any).payoutTable,
          [field]: value,
        },
      },
    }));
  };

  const b2cEarningSum = {
    perthreshold: formData.b2c.kpis.reduce(
      (s, k) => s + (k.performance?.threshold ?? 0),
      0,
    ),
    pertarget: formData.b2c.kpis.reduce(
      (s, k) => s + (k.performance?.target ?? 0),
      0,
    ),
    perstretch: formData.b2c.kpis.reduce(
      (s, k) => s + (k.performance?.stretch ?? 0),
      0,
    ),
    ernthreshold: formData.b2c.kpis.reduce(
      (s, k) =>
        k.earningType !== "Credit Days" ? s + (k.earning?.threshold ?? 0) : s,
      0,
    ),
    erntarget: formData.b2c.kpis.reduce(
      (s, k) =>
        k.earningType !== "Credit Days" ? s + (k.earning?.target ?? 0) : s,
      0,
    ),
    ernstretch: formData.b2c.kpis.reduce(
      (s, k) =>
        k.earningType !== "Credit Days" ? s + (k.earning?.stretch ?? 0) : s,
      0,
    ),
  };

  const updateTop = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const currentSection = formData[sectionKey];
  const kpis = currentSection.kpis as KpiRow[];

  const numColStyle: React.CSSProperties = {
    width: "150px",
    textAlign: "center",
  };
  const dropColStyle: React.CSSProperties = { width: "110px" };

  const numberCell = (val: number, onChange: (v: number) => void) => (
    <InputNumber
      value={val}
      onValueChange={(e) => onChange(e.value ?? 0)}
      minFractionDigits={6}
      maxFractionDigits={6}
      style={{ width: "100%" }}
      inputStyle={{
        textAlign: "right",
        padding: "2px 4px",
        fontSize: "0.8rem",
      }}
    />
  );

  /* Credit Days values — integer only (0 decimal places) */
  const integerCell = (val: number, onChange: (v: number) => void) => (
    <InputNumber
      value={val}
      onValueChange={(e) => onChange(e.value ?? 0)}
      minFractionDigits={0}
      maxFractionDigits={0}
      style={{ width: "100%" }}
      inputStyle={{
        textAlign: "right",
        padding: "2px 4px",
        fontSize: "0.8rem",
      }}
    />
  );

  /* Disabled read-only */
  const disabledCell = (val: number, fractionDigits = 6) => (
    <InputNumber
      value={val}
      disabled
      minFractionDigits={fractionDigits}
      maxFractionDigits={fractionDigits}
      style={{ width: "100%" }}
      inputStyle={{
        textAlign: "right",
        padding: "2px 4px",
        fontSize: "0.8rem",
        backgroundColor: "#d0d0d0",
        color: "#555",
        cursor: "not-allowed",
      }}
    />
  );

  /* Payout footer input (HDSSD / Industrial) */
  const payoutInputCell = (
    val: number,
    field: keyof PerformanceTarget,
    sk: "hsssd" | "industrial",
  ) => (
    <InputNumber
      value={val}
      onValueChange={(e) => updatePayoutTable(sk, field, e.value ?? 0)}
      minFractionDigits={6}
      maxFractionDigits={6}
      style={{ width: "100%" }}
      inputStyle={{
        textAlign: "right",
        padding: "2px 4px",
        fontSize: "0.8rem",
      }}
    />
  );

  const b2cHeaderGroup = (
    <ColumnGroup>
      <Row>
        <Column header="Parameter" rowSpan={2} />
        <Column header="Type" rowSpan={2} />
        <Column header="Applicable On" rowSpan={2} />
        <Column header="Basis" rowSpan={2} />
        <Column header="Performance Target (As per Basis)" colSpan={3} />
        <Column header="Earning Type" rowSpan={2} />
        <Column header="Earning (As per Basis)" colSpan={3} />
      </Row>
      <Row>
        <Column header="Threshold" />
        <Column header="Target" />
        <Column header="Stretch" />
        <Column header="Threshold" />
        <Column header="Target" />
        <Column header="Stretch" />
      </Row>
    </ColumnGroup>
  );

  const otherHeaderGroup = (
    <ColumnGroup>
      <Row>
        <Column header="Parameter" rowSpan={2} />
        <Column header="Type" rowSpan={2} />
        <Column header="Applicable On" rowSpan={2} />
        <Column header="ODT" rowSpan={2} />
        <Column header="Basis" rowSpan={2} />
        <Column header="Weightage" rowSpan={2} />
        <Column header="Performance Target (As per Basis)" colSpan={4} />
      </Row>
      <Row>
        <Column header="Threshold" />
        <Column header="Target" />
        <Column header="Stretch" />
        <Column header="Mega" />
      </Row>
    </ColumnGroup>
  );

  /* PAYOUT FOOTER (B2C)*/
  const b2cPayoutFooter = (
    <ColumnGroup>
      <Row>
        <Column
          footer="Payout Table:"
          colSpan={4}
          footerStyle={{
            ...payoutRowStyle,
            textAlign: "left",
            paddingLeft: "8px",
          }}
        />
        {/* Performance sums — disabled */}
        <Column
          footer={disabledCell(b2cEarningSum.perthreshold)}
          footerStyle={{ ...payoutRowStyle, padding: "2px" }}
        />
        <Column
          footer={disabledCell(b2cEarningSum.pertarget)}
          footerStyle={{ ...payoutRowStyle, padding: "2px" }}
        />
        <Column
          footer={disabledCell(b2cEarningSum.perstretch)}
          footerStyle={{ ...payoutRowStyle, padding: "2px" }}
        />
        {/* Earning Type — blank cell */}
        <Column footer="" footerStyle={{ ...payoutRowStyle, padding: "2px" }} />
        {/* Earning sums — disabled */}
        <Column
          footer={disabledCell(b2cEarningSum.ernthreshold)}
          footerStyle={{ ...payoutRowStyle, padding: "2px" }}
        />
        <Column
          footer={disabledCell(b2cEarningSum.erntarget)}
          footerStyle={{ ...payoutRowStyle, padding: "2px" }}
        />
        <Column
          footer={disabledCell(b2cEarningSum.ernstretch)}
          footerStyle={{ ...payoutRowStyle, padding: "2px" }}
        />
      </Row>
    </ColumnGroup>
  );

  /* PAYOUT FOOTER (HDSSD / INDUSTRIAL) */
  const sk = sectionKey as "hsssd" | "industrial";
  const payoutLabel =
    constructType !== "B2C" ? (formData[sk] as any).payoutLabel : "";
  const payoutTable =
    constructType !== "B2C" ? (formData[sk] as any).payoutTable : null;

  const otherPayoutFooter = payoutTable ? (
    <ColumnGroup>
      <Row>
        <Column
          footer={payoutLabel}
          colSpan={6}
          footerStyle={{
            ...payoutRowStyle,
            textAlign: "left",
            paddingLeft: "8px",
            fontSize: "0.78rem",
          }}
        />
        <Column
          footer={payoutInputCell(payoutTable.threshold ?? 0, "threshold", sk)}
          footerStyle={{ ...payoutRowStyle, padding: "2px" }}
        />
        <Column
          footer={payoutInputCell(payoutTable.target ?? 0, "target", sk)}
          footerStyle={{ ...payoutRowStyle, padding: "2px" }}
        />
        <Column
          footer={payoutInputCell(payoutTable.stretch ?? 0, "stretch", sk)}
          footerStyle={{ ...payoutRowStyle, padding: "2px" }}
        />
        <Column
          footer={payoutInputCell(payoutTable.mega ?? 0, "mega", sk)}
          footerStyle={{ ...payoutRowStyle, padding: "2px" }}
        />
      </Row>
    </ColumnGroup>
  ) : null;

  return (
    <div>
      <div
        className="border rounded mb-3 p-3"
        style={{ background: "#f7f7f7" }}
      >
        <div className="row">
          <div className="col-lg-3 col-md-6 col-sm-12">
            <div className="d-block mb-2">
              <label className="form-label">
                Construct Name <span className="mandatory">*</span>
              </label>
              <InputText
                value={formData.constructName}
                onChange={(e) => updateTop("constructName", e.target.value)}
                className="form-control"
              />
            </div>
          </div>

          <div className="col-lg-3 col-md-6 col-sm-12">
            <div className="d-block mb-2">
              <label className="form-label">
                Start Date <span className="mandatory">*</span>
              </label>
              <Calendar
                value={formData.startDate}
                onChange={(e) => updateTop("startDate", e.value)}
                showIcon
                placeholder="From Date"
              />
            </div>
          </div>

          <div className="col-lg-3 col-md-6 col-sm-12">
            <div className="d-block mb-2">
              <label className="form-label">
                End Date <span className="mandatory">*</span>
              </label>
              <Calendar
                value={formData.endDate}
                onChange={(e) => updateTop("endDate", e.value)}
                showIcon
                placeholder="To Date"
              />
            </div>
          </div>

          {constructType == "B2C" && (
            <div className="col-lg-3 col-md-6 col-sm-12">
              <div className="d-block mb-2">
                <label className="form-label">Order Mode</label>
                <MultiSelect
                  value={formData.orderMode}
                  options={orderModeOptions}
                  optionLabel="ORD_MODE_DESC"
                  dataKey="ORD_MODE_ID"
                  onChange={(e) => updateTop("orderMode", e.value)}
                  placeholder="Select order type"
                  style={{ width: "100%" }}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="DataTable">
        {isB2C ? (
          <DataTable
            value={kpis}
            headerColumnGroup={b2cHeaderGroup}
            footerColumnGroup={b2cPayoutFooter}
            responsiveLayout="scroll"
            tableStyle={{ minWidth: "100rem" }}
          >
            <Column field="parameter" style={{ minWidth: "18rem" }} />
            <Column
              style={dropColStyle}
              body={(row: KpiRow) => (
                <Dropdown
                  value={row.type}
                  options={B2C_TYPE_OPTIONS}
                  onChange={(e) => updateKpi(row.id, "type", e.value)}
                  style={{ width: "100%" }}
                />
              )}
            />
            <Column
              style={dropColStyle}
              body={(row: KpiRow) => (
                <Dropdown
                  value={row.applicableOn}
                  options={APPLICABLE_OPTIONS_B2C}
                  onChange={(e) => updateKpi(row.id, "applicableOn", e.value)}
                  style={{ width: "100%" }}
                />
              )}
            />
            <Column
              style={dropColStyle}
              body={(row: KpiRow) => (
                <Dropdown
                  value={row.basis}
                  options={BASIS_OPTIONS}
                  onChange={(e) => updateKpi(row.id, "basis", e.value)}
                  style={{ width: "100%" }}
                />
              )}
            />
            <Column
              style={numColStyle}
              body={(row: KpiRow) =>
                numberCell(row.performance.threshold, (v) =>
                  updatePerformance(row.id, "threshold", v),
                )
              }
            />
            <Column
              style={numColStyle}
              body={(row: KpiRow) =>
                numberCell(row.performance.target, (v) =>
                  updatePerformance(row.id, "target", v),
                )
              }
            />
            <Column
              style={numColStyle}
              body={(row: KpiRow) =>
                numberCell(row.performance.stretch, (v) =>
                  updatePerformance(row.id, "stretch", v),
                )
              }
            />
            <Column
              style={{ ...dropColStyle, width: "130px" }}
              body={(row: KpiRow) => (
                <Dropdown
                  value={row.earningType ?? "Earning %"}
                  options={EARNING_TYPE_OPTIONS}
                  onChange={(e) =>
                    updateKpi(row.id, "earningType", e.value as EarningType)
                  }
                  style={{ width: "100%" }}
                />
              )}
            />

            <Column
              style={numColStyle}
              body={(row: KpiRow) =>
                row.earningType === "Credit Days"
                  ? integerCell(row.earningCreditDays?.threshold ?? 0, (v) =>
                      updateEarningCreditDays(row.id, "threshold", v),
                    )
                  : numberCell(row.earning?.threshold ?? 0, (v) =>
                      updateEarning(row.id, "threshold", v),
                    )
              }
            />

            <Column
              style={numColStyle}
              body={(row: KpiRow) =>
                row.earningType === "Credit Days"
                  ? integerCell(row.earningCreditDays?.target ?? 0, (v) =>
                      updateEarningCreditDays(row.id, "target", v),
                    )
                  : numberCell(row.earning?.target ?? 0, (v) =>
                      updateEarning(row.id, "target", v),
                    )
              }
            />

            <Column
              style={numColStyle}
              body={(row: KpiRow) =>
                row.earningType === "Credit Days"
                  ? integerCell(row.earningCreditDays?.stretch ?? 0, (v) =>
                      updateEarningCreditDays(row.id, "stretch", v),
                    )
                  : numberCell(row.earning?.stretch ?? 0, (v) =>
                      updateEarning(row.id, "stretch", v),
                    )
              }
            />
          </DataTable>
        ) : (
          /* HDSSD / INDUSTRIAL TABLE */
          <DataTable
            value={kpis}
            headerColumnGroup={otherHeaderGroup}
            footerColumnGroup={otherPayoutFooter ?? undefined}
            responsiveLayout="scroll"
            tableStyle={{ minWidth: "100rem" }}
          >
            <Column field="parameter" style={{ minWidth: "18rem" }} />
            <Column
              style={dropColStyle}
              body={(row: KpiRow) => (
                <Dropdown
                  value={row.type}
                  options={B2C_TYPE_OPTIONS}
                  onChange={(e) => updateKpi(row.id, "type", e.value)}
                  style={{ width: "100%" }}
                />
              )}
            />
            <Column
              style={dropColStyle}
              body={(row: KpiRow) => (
                <Dropdown
                  value={row.applicableOn}
                  options={APPLICABLE_OPTIONS}
                  onChange={(e) => updateKpi(row.id, "applicableOn", e.value)}
                  style={{ width: "100%" }}
                />
              )}
            />
            <Column
              style={{ width: "90px" }}
              body={(row: KpiRow) => (
                <Dropdown
                  value={row.odt ?? "NO"}
                  options={ODT_OPTIONS}
                  onChange={(e) => updateKpi(row.id, "odt", e.value)}
                  style={{ width: "100%" }}
                />
              )}
            />
            <Column
              style={dropColStyle}
              body={(row: KpiRow) => (
                <Dropdown
                  value={row.basis}
                  options={BASIS_OPTIONS}
                  onChange={(e) => updateKpi(row.id, "basis", e.value)}
                  style={{ width: "100%" }}
                />
              )}
            />
            <Column
              style={numColStyle}
              body={(row: KpiRow) =>
                numberCell(row.weightage ?? 0, (v) =>
                  updateKpi(row.id, "weightage", v),
                )
              }
            />
            <Column
              style={numColStyle}
              body={(row: KpiRow) =>
                numberCell(row.performance.threshold, (v) =>
                  updatePerformance(row.id, "threshold", v),
                )
              }
            />
            <Column
              style={numColStyle}
              body={(row: KpiRow) =>
                numberCell(row.performance.target, (v) =>
                  updatePerformance(row.id, "target", v),
                )
              }
            />
            <Column
              style={numColStyle}
              body={(row: KpiRow) =>
                numberCell(row.performance.stretch, (v) =>
                  updatePerformance(row.id, "stretch", v),
                )
              }
            />
            <Column
              style={numColStyle}
              body={(row: KpiRow) =>
                numberCell(row.performance.mega ?? 0, (v) =>
                  updatePerformance(row.id, "mega", v),
                )
              }
            />
          </DataTable>
        )}
      </div>
    </div>
  );
}
